import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootReducerState } from '../../redux/Reducers';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { ReleaseNotification, ReleasePortalType } from '../../types/release-notes/release-notes';
import { PaginationType } from '../../types/utils/pagination.type';
import { rejectWithApiError } from '../../utils/api';
import { NOTIFICATION_LIMIT } from './releaseNotifications.config';

const releaseNotificationsUrl = `${process.env.REACT_APP_API_URL}/release-notifications`;

export const fetchNextPageUnread = createAsyncThunk<
  PaginationType<ReleaseNotification>,
  void,
  { state: RootReducerState; rejectValue: string }
>(
  'releaseNotifications/fetchNextPageUnread/no-loader',
  async (_, thunkAPI) => {
    try {
      const nextPage = thunkAPI.getState().releaseNotificationsReducer.nextPageUnread;
      const response = await axios.get<PaginationType<ReleaseNotification>>(releaseNotificationsUrl, {
        params: {
          is_read: false,
          portal_type: ReleasePortalType.TELCLOUD,
          limit: NOTIFICATION_LIMIT,
          page: nextPage,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Could not fetch unread notifications!' });
    }
  },
  {
    condition: (_, { getState }) => {
      const { statusUnread, nextPageUnread, totalPagesUnread } = getState().releaseNotificationsReducer;
      const hasNextPage = totalPagesUnread === null || nextPageUnread <= totalPagesUnread;
      return statusUnread !== FETCH_STATUS.PENDING && hasNextPage;
    },
  }
);

export const fetchNextPageAll = createAsyncThunk<
  PaginationType<ReleaseNotification>,
  void,
  { state: RootReducerState; rejectValue: string }
>(
  'releaseNotifications/fetchNextPageAll/no-loader',
  async (_, thunkAPI) => {
    try {
      const nextPage = thunkAPI.getState().releaseNotificationsReducer.nextPageAll;
      const response = await axios.get<PaginationType<ReleaseNotification>>(releaseNotificationsUrl, {
        params: {
          portal_type: ReleasePortalType.TELCLOUD,
          limit: NOTIFICATION_LIMIT,
          page: nextPage,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Could not fetch all notifications!' });
    }
  },
  {
    condition: (_, { getState }) => {
      const { statusAll, nextPageAll, totalPagesAll } = getState().releaseNotificationsReducer;
      const hasNextPage = totalPagesAll === null || nextPageAll <= totalPagesAll;
      return statusAll !== FETCH_STATUS.PENDING && hasNextPage;
    },
  }
);

export const updateNotification = createAsyncThunk<
  void,
  { notificationId: number; isRead: boolean },
  { state: RootReducerState; rejectValue: string }
>('releaseNotifications/updateNotification', async ({ notificationId, isRead }, thunkAPI) => {
  try {
    await axios.patch(`${process.env.REACT_APP_API_URL}/release-notifications/${notificationId}`, { isRead });
    await thunkAPI.dispatch(refetchUnreadNotifications());
  } catch (error) {
    return rejectWithApiError({
      thunkAPI,
      error,
      fallback: `Could not mark notification as ${isRead ? 'read' : 'unread'}!`,
    });
  }
});

export const markAllNotificationsAsRead = createAsyncThunk<
  void,
  void,
  { state: RootReducerState; rejectValue: string }
>('releaseNotifications/markAllNotificationsAsRead', async (_, thunkAPI) => {
  try {
    await axios.post(`${process.env.REACT_APP_API_URL}/release-notifications/mark-all-as-read`);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not mark all notifications as read!' });
  }
});

export const refetchUnreadNotifications = createAsyncThunk<
  PaginationType<ReleaseNotification>,
  void,
  { state: RootReducerState; rejectValue: string }
>('releaseNotifications/refetchUnreadNotifications', async (_, thunkAPI) => {
  try {
    const currentUnread = thunkAPI.getState().releaseNotificationsReducer.itemsUnread.length;
    const limit = Math.ceil(currentUnread / NOTIFICATION_LIMIT) * NOTIFICATION_LIMIT;
    const response = await axios.get<PaginationType<ReleaseNotification>>(releaseNotificationsUrl, {
      params: {
        is_read: false,
        portal_type: ReleasePortalType.TELCLOUD,
        limit,
      },
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not refetch unread notifications!' });
  }
});

export const refetchAllNotifications = createAsyncThunk<
  PaginationType<ReleaseNotification>,
  void,
  { state: RootReducerState; rejectValue: string }
>('releaseNotifications/refetchAllNotifications', async (_, thunkAPI) => {
  try {
    await thunkAPI.dispatch(refetchUnreadNotifications());

    const currentAll = thunkAPI.getState().releaseNotificationsReducer.itemsAll.length;
    const limit = Math.ceil(currentAll / NOTIFICATION_LIMIT) * NOTIFICATION_LIMIT;
    const response = await axios.get<PaginationType<ReleaseNotification>>(releaseNotificationsUrl, {
      params: {
        portal_type: ReleasePortalType.TELCLOUD,
        limit,
      },
    });
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Could not refetch all notifications!' });
  }
});
